import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MapPin, Building2, Leaf, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { REGIONS, SOUTH_INDIA_MAP, NETWORK_CITIES } from "@/lib/site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-avatar-ink/40">
      {label}
    </span>
    <input
      {...props}
      className="w-full border-b border-avatar-ink/20 bg-transparent py-3 text-avatar-ink outline-none transition-colors duration-300 placeholder:text-avatar-ink/30 focus:border-avatar-gold"
    />
  </label>
);

export const DealerNetwork = () => {
  const [region, setRegion] = useState(REGIONS[0]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", city: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { type: "dealer", region, ...form });
      toast.success("Application received. Our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", company: "", city: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="dealers" className="relative mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-32">
      {/* Reach showcase */}
      <div className="mb-24 grid grid-cols-1 items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
        <Reveal>
          <div className="overflow-hidden rounded-3xl border border-avatar-ink/10 bg-black">
            <img
              src={SOUTH_INDIA_MAP}
              alt="Avatar distribution network across South India"
              loading="lazy"
              className="mx-auto block w-full object-contain"
            />
          </div>
        </Reveal>

        <div>
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-avatar-gold">
              (Our Reach)
            </p>
            <h2 className="font-heading text-4xl font-bold leading-none tracking-tighter text-avatar-ink md:text-6xl">
              Growing across<br />South India.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-avatar-ink/55">
              A trusted distribution network spanning Tamil Nadu, Kerala, Andhra
              Pradesh and Karnataka — and expanding every quarter.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-3 gap-6 border-y border-avatar-ink/10 py-8">
            {[
              { icon: Building2, v: "20+", l: "Major Cities" },
              { icon: Leaf, v: "100%", l: "Virgin Plastic" },
              { icon: ShieldCheck, v: "3-5", l: "Layer Protection" },
            ].map((s) => (
              <div key={s.l}>
                <s.icon size={20} className="mb-3 text-avatar-gold" />
                <div className="font-heading text-3xl font-bold tracking-tight text-avatar-ink">{s.v}</div>
                <div className="mt-1 text-xs text-avatar-ink/45">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {NETWORK_CITIES.map((c) => (
              <span key={c} className="rounded-full border border-avatar-ink/12 px-3 py-1.5 text-xs text-avatar-ink/60">
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
        <Reveal>
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-avatar-red">
            (Dealer Network)
          </p>
          <h2 className="font-heading text-4xl font-bold leading-none tracking-tighter text-avatar-ink md:text-6xl">
            Join the<br />network.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-avatar-ink/55">
            Partner with a fast-growing premium brand. Avatar dealers get quality
            product, marketing support and reliable on-time delivery.
          </p>

          <div className="mt-12 space-y-1">
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.2em] text-avatar-ink/40">
              Currently serving
            </p>
            {REGIONS.map((r) => (
              <div key={r} className="flex items-center gap-3 border-t border-avatar-ink/10 py-4">
                <MapPin size={16} className="text-avatar-gold" />
                <span className="font-heading text-xl font-semibold text-avatar-ink">{r}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="dealer-form" className="space-y-7 rounded-3xl border border-avatar-ink/10 bg-avatar-surface/40 p-8 backdrop-blur-sm md:p-10">
            <div className="grid grid-cols-1 gap-7 md:grid-cols-2">
              <Field label="Full Name" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="dealer-name" />
              <Field label="Email" type="email" placeholder="you@email.com" value={form.email} onChange={set("email")} data-testid="dealer-email" />
              <Field label="Phone" placeholder="+91" value={form.phone} onChange={set("phone")} data-testid="dealer-phone" />
              <Field label="Company" placeholder="Business name" value={form.company} onChange={set("company")} data-testid="dealer-company" />
              <Field label="City" placeholder="City" value={form.city} onChange={set("city")} data-testid="dealer-city" />
              <label className="block">
                <span className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-avatar-ink/40">Region</span>
                <select
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  data-testid="dealer-region"
                  className="w-full border-b border-avatar-ink/20 bg-transparent py-3 text-avatar-ink outline-none transition-colors duration-300 focus:border-avatar-gold"
                >
                  {REGIONS.map((r) => (
                    <option key={r} value={r} className="bg-white text-avatar-ink">{r}</option>
                  ))}
                </select>
              </label>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="dealer-submit"
              className="group inline-flex items-center gap-2 rounded-full bg-avatar-red px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#c62828] disabled:opacity-60"
            >
              {loading ? "Submitting..." : "Apply to Become a Dealer"}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
